import { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import * as authService from '../services/auth'
import { LoginCredentials } from '../services/auth'

type UserRole = 'admin' | 'super_admin' | 'participant'

interface AuthContextType {
  isAuthenticated: boolean
  user: { email: string; role: UserRole } | null
  token: string | null
  login: (credentials: LoginCredentials) => Promise<void>
  register: (credentials: LoginCredentials & { role: UserRole }) => Promise<void>
  logout: () => Promise<void>
  error: string | null
  loading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<{ email: string; role: UserRole } | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('token')
      if (savedToken) {
        try {
          setToken(savedToken)
          const response = await authService.getCurrentUser(savedToken)
          setUser({ email: response.user.email, role: response.user.role })
          setIsAuthenticated(true)
        } catch (err) {
          localStorage.removeItem('token')
          setToken(null)
          setUser(null)
          setIsAuthenticated(false)
        }
      }
      setLoading(false)
    }

    initializeAuth()
  }, [])

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null)
      const response = await authService.login(credentials)
      setUser({ email: response.user.email, role: response.user.role })
      setToken(response.token)
      setIsAuthenticated(true)
      localStorage.setItem('token', response.token)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      throw err
    }
  }

  const register = async (credentials: LoginCredentials & { role: UserRole }) => {
    try {
      setError(null)
      const response = await authService.register(credentials)
      setUser({ email: response.user.email, role: response.user.role })
      setToken(response.token)
      setIsAuthenticated(true)
      localStorage.setItem('token', response.token)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      throw err
    }
  }

  const logout = async () => {
    try {
      setError(null)
      if (token) {
        await authService.logout(token)
      }
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
      setIsAuthenticated(false)
    }
  }

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      token,
      login,
      register,
      logout,
      error,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  )
}
